// ⚠️ WARNING: Whoever touches the nav bar behaviour - is gay⚠️
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navLinks = [
  { name: 'How it works', path: '/technology' },
  { name: 'Research', path: '/research' },
  { name: 'App', path: '/app' },
];

// Desktop nav links component
const DesktopNavLinks = () => (
  // Use absolute centering to ensure true center regardless of side element widths
  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-10 nav-links-container group">
    {navLinks.map((link) => (
      <NavLink
        key={link.path}
        to={link.path}
        className={({ isActive }) =>
          `nav-link text-base font-medium tracking-wide transition-opacity hover:underline underline-offset-4 decoration-1 text-white group-hover:text-white/60 hover:!text-white ${isActive ? 'underline' : ''}`
        }
      >
        {link.name}
      </NavLink>
    ))}
  </div>
);

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Detect if Hero section is visible using Intersection Observer
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let retryCount = 0;
    const maxRetries = 5;

    const setupObserver = () => {
      const heroElement = document.getElementById('hero');

      if (!heroElement) {
        // No hero on this page - default to glass navbar
        if (retryCount < maxRetries) {
          retryCount++;
          const retryTimeout = setTimeout(setupObserver, 100);
          return () => clearTimeout(retryTimeout);
        } else {
          // After retries, assume no hero exists on this page
          setIsAtTop(false);
          return;
        }
      }

      // Hero found - set initial state to true since we're at the top
      setIsAtTop(true);

      // Set up observer for subsequent changes
      observer = new IntersectionObserver(
        ([entry]) => {
          setIsAtTop(entry.isIntersecting);
        },
        {
          threshold: 0.1, // Trigger when at least 10% of hero is visible
          rootMargin: '0px 0px 0px 0px'
        }
      );

      observer.observe(heroElement);
    };

    // Set up observer
    const cleanup = setupObserver();

    return () => {
      if (typeof cleanup === 'function') cleanup();
      if (observer) observer.disconnect();
    };
  }, [location.pathname]); // Re-run when route changes

  // Handle scroll for sticky behavior (hide on scroll down, show on scroll up)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const lastScrollY = lastScrollYRef.current;

      // Determine if background should be scrolled (solid-ish)
      const scrolled = currentScrollY > 50;
      if (scrolled !== isScrolled) setIsScrolled(scrolled);

      // Smart Sticky Logic - always active
      if (currentScrollY > lastScrollY && currentScrollY > 20) {
        if (isVisible) setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        if (!isVisible) setIsVisible(true);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible, isScrolled, isAtTop]);

  // Handle Escape key to close mobile menu
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  return (
    <>
      {/* Navbar - transparent in Hero, glass outside */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 pointer-events-none safe-top transition-transform duration-500 ease-in-out ${isVisible || isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
      >
        {/* Navigation Content */}
        <nav
          className={`relative pointer-events-auto flex items-center justify-between h-20 px-6 md:px-12 transition-all duration-300 ${!isAtTop || isMobileMenuOpen
            ? 'bg-black/90 backdrop-blur-lg'
            : 'bg-transparent'
            }`}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Left: Logo */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="relative text-4xl md:text-2xl font-normal md:font-semibold tracking-tight transition-colors hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-4 focus:outline-none z-10 text-white focus-visible:outline-white"
            aria-label="Nura Home"
          >
            <span className="invisible" aria-hidden="true">Nura</span>
            <img src="/nura-wordmark-white.svg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain" />
          </Link>

          {/* Desktop Navigation Links */}
          <DesktopNavLinks />

          {/* Right: Actions */}
          <div className="hidden md:flex items-center gap-6 z-10">
            <Link
              to="/app"
              className={`text-lg font-medium tracking-wide bg-transparent px-5 py-2 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus:outline-none text-white border border-white hover:bg-white hover:text-black focus-visible:outline-white`}
            >
              Try Nura
            </Link>
          </div>

          {/* Mobile Menu Button Container */}
          <div className="md:hidden flex items-center gap-4 z-50">
            <button
              ref={menuButtonRef}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 -mr-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus:outline-none text-white focus-visible:outline-white"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden bg-black/95 backdrop-blur-xl safe-top"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <div className="flex flex-col items-start justify-start h-full px-6 gap-6 pt-32">
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="text-4xl font-normal tracking-[-0.02em] text-white hover:text-white/60 transition-colors leading-[1.1]"
            >
              Home
            </Link>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={closeMobileMenu}
                className="text-4xl font-normal tracking-[-0.02em] text-white hover:text-white/60 transition-colors leading-[1.1]"
              >
                {link.name}
              </Link>
            ))}

            <Link
              to="/app"
              onClick={closeMobileMenu}
              className="text-4xl font-normal tracking-[-0.02em] text-white hover:text-white/60 transition-colors leading-[1.1]"
            >
              Try Nura
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
